import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { notificationFilterById } from "../../../../../actions/Utilities/NotificationFilter";
import AddButton from "../../../../../components/AddButton";
import ConfirmBoxDelete from "../../../../../components/ConfirmBoxDelete";
import EditButton from "../../../../../components/EditButton";
import MaterialTableTS from "../../../../../components/tables/MaterialTableTS";
import Timer from "../../Timer";
import { permissionsSelector } from "../../../../Login/selectors/permissionSelector";
import { PERMISSIONS } from "../../../../PermissionsConstants";
import TableFilters from "../../TableFilters";
import {
  addStatePaymentRemittanceType,
  editStatePaymentRemittanceType,
  filterText,
  rowForEditPaymentRemittanceType,
  selectedRowsStatePaymentRemittanceType,
} from "../atoms/atoms";
import PaymentRemittanceTypeForm from "../forms/PaymentRemittanceTypeForm";
import useDeletePaymentRemittanceType from "../hooks/UseDeletePaymentRemittanceType";
import { useAgmtPaymentRemittanceType } from "../hooks/useGetPaymentRemittanceType";
import { PaymentRemittanceTypeInfo } from "../types";

export default function PaymentRemittanceTypeTable() {
  //Hooks Call
  const {
    data: OriginalData,
    isLoading: isFetching,
    isSuccess: paymentRemittanceTypeSuccess,
    refetch,
  } = useAgmtPaymentRemittanceType();

  const BulkDeletePaymentRemittanceType = useDeletePaymentRemittanceType();

  //Atoms Call
  const [actionAddState, setActionAddState] = useRecoilState(
    addStatePaymentRemittanceType
  );
  const [selectedRows, setSelectedRows] = useRecoilState(
    selectedRowsStatePaymentRemittanceType
  );
  const [actionEditState, setActionEditState] = useRecoilState(
    editStatePaymentRemittanceType
  );
  const [filterTextState, setFilterTextState] = useRecoilState(filterText);
  const setEditRowData = useSetRecoilState(rowForEditPaymentRemittanceType);
  const userPermissions = useRecoilValue(permissionsSelector);

  //Local States
  const [paymentRemittanceTypeData, setPaymentRemittanceTypeData] = useState<
    PaymentRemittanceTypeInfo[]
  >([]);
  const [rowsCount, setRowsCount] = useState(0);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [
    paymentRemittanceTypeFilteredData,
    setPaymentRemittanceTypeFilteredData,
  ] = useState<PaymentRemittanceTypeInfo[]>([]);

  // Permission States
  const addPermission =
    PERMISSIONS.permission_utilities_configureagreement_paymentremittancetype_add;
  const editPermission =
    PERMISSIONS.permission_utilities_configureagreement_paymentremittancetype_edit;
  const deletePermission =
    PERMISSIONS.permission_utilities_configureagreement_paymentremittancetype_delete;

  //For filtered data update
  const [paymentRemittanceTypeId, setPaymentRemittanceTypeId] = useState("");

  useEffect(() => {
    if (paymentRemittanceTypeId !== "") {
      if (paymentRemittanceTypeId?.includes(".")) {
        let ids = paymentRemittanceTypeId.split(".");
        const arr = ids.map((id) => Number(id));
        const filteredData = paymentRemittanceTypeData?.filter((x) =>
          arr.includes(x.paymentRemittanceTypeId)
        );
        setPaymentRemittanceTypeFilteredData(filteredData);
      } else {
        const filteredData = paymentRemittanceTypeData?.filter(
          (x) => x.paymentRemittanceTypeId === Number(paymentRemittanceTypeId)
        );
        setPaymentRemittanceTypeFilteredData(filteredData);
      }
    }
  }, [paymentRemittanceTypeId, paymentRemittanceTypeData]);

  useEffect(() => {
    if (paymentRemittanceTypeSuccess && OriginalData) {
      setPaymentRemittanceTypeData(OriginalData.map((data) => ({ ...data })));
    }
  }, [OriginalData, paymentRemittanceTypeSuccess]);

  let location = useLocation();
  const history = useHistory();
  let recordIdFromRecent;

  if (location.state !== null) {
    recordIdFromRecent = location.state;
  }

  useEffect(() => {
    if (recordIdFromRecent) {
      try {
        notificationFilterById(
          recordIdFromRecent.recordId,
          recordIdFromRecent.moduleName,
          recordIdFromRecent.subModuleName
        ).then((result) => {
          setPaymentRemittanceTypeFilteredData(result);
          setPaymentRemittanceTypeId(recordIdFromRecent.recordId);
        });
      } catch (error) {}
    } else {
      localStorage.removeItem("NotifFilter");
      localStorage.removeItem("ModuleFilter");
    }
  }, [history, recordIdFromRecent]);

  const handleAdd = () => {
    setActionAddState(true);
  };

  const handleSelectedRowsChange = (rows) => {
    const forDelete = rows
      .filter((x) => x.status)
      .map((r) => {
        const { tableData, ...rows } = r;
        return rows;
      });
    setSelectedRows(forDelete);
    setRowsCount(forDelete.length);
  };

  const handleEdit = (rowData) => {
    delete rowData.tableData;
    setEditRowData({ ...rowData });
    setActionEditState(true);
  };

  const handleDelete = async () => {
    const ids = selectedRows
      ? selectedRows.map((row) => row.paymentRemittanceTypeId)
      : undefined;
    BulkDeletePaymentRemittanceType(ids);
    setSelectedRows([]);
    setOpenModalDelete(false);
    await Timer();
    refetch();
  };

  const deleteMessage = (
    <div className="text-gray-500 font-normal">{`Are you sure you want to deactivate ${rowsCount} ${
      rowsCount > 1 ? "PaymentRemittanceTypes" : "PaymentRemittanceType"
    }?`}</div>
  );

  const filteredItems = paymentRemittanceTypeData
    ? paymentRemittanceTypeData?.filter(
        (item) =>
          item.paymentRemittanceTypeName &&
          item.paymentRemittanceTypeName
            .toLowerCase()
            .includes(filterTextState?.trim().toLowerCase())
      )
    : [];

  const matColumns = [
    {
      title: "Payment Remittance Type Name",
      field: "paymentRemittanceTypeName",
      customSort: (a, b) =>
        a.paymentRemittanceTypeName && b.paymentRemittanceTypeName
          ? a.paymentRemittanceTypeName
              .trim()
              .localeCompare(b.paymentRemittanceTypeName.trim())
          : a.paymentRemittanceTypeName
          ? 0
          : -1,
    },
    {
      title: "Created Date",
      field: "createdDate",
      type: "date" as "date",
    },
    {
      title: "Created By",
      field: "createdBy",
      customSort: (a, b) =>
        a.createdBy && b.createdBy
          ? a.createdBy.trim().localeCompare(b.createdBy.trim())
          : a.createdBy
          ? 0
          : -1,
    },

    {
      title: "Modified By",
      field: "modifiedBy",
      customSort: (a, b) =>
        a.modifiedBy && b.modifiedBy
          ? a.modifiedBy.trim().localeCompare(b.modifiedBy.trim())
          : a.modifiedBy
          ? 0
          : -1,
    },
    {
      title: "Modified Date",
      field: "modifiedDate",
      type: "date" as "date",
    },
    {
      title: "Status",
      field: "status",
      render: (rowData) => {
        return rowData?.status ? "Active" : "Inactive";
      },
    },
    {
      title: "Action/s",
      align: "center" as "center",
      hidden: !userPermissions.includes(editPermission),
      render: (rowData) => {
        return (
          <div className="flex items-center place-content-center mr-5">
            <EditButton
              className={""}
              disabled={
                actionEditState ||
                (selectedRows !== undefined && selectedRows.length > 0)
              }
              onClick={() => {
                handleEdit(rowData);
              }}
            />
          </div>
        );
      },
    },
  ];
  return (
    <>
      <div className="bg-white p-4">
        {actionAddState || actionEditState ? (
          <div>
            <PaymentRemittanceTypeForm refetch={refetch} />
          </div>
        ) : (
          <>
            <div className="flex flex-row justify-between items-center">
              <TableFilters
                moduleDetails={"PaymentRemittanceType"}
                textInputName={"PaymentRemittanceTypeName"}
                columnDetails={"Payment Remittance Type Name"}
                filterText={setFilterTextState}
                disabledButton={
                  selectedRows !== undefined && selectedRows.length > 0
                }
                disabledTextBox={
                  selectedRows !== undefined && selectedRows.length > 0
                }
              />

              <div>
                {userPermissions.includes(addPermission) ? (
                  <>
                    <AddButton
                      buttonText="New Payment Remittance Type"
                      onClick={() => handleAdd()}
                      disabled={
                        selectedRows !== undefined && selectedRows.length > 0
                      }
                    />
                  </>
                ) : (
                  ""
                )}
              </div>
            </div>
            <div>
              {userPermissions.includes(deletePermission) ? (
                <div className="text-secondary italic text-xs3 my-2">
                  <p className="font-bold italic inline-block mr-1">Note:</p>
                  <span>Tick the box to deactivate some data.</span>
                </div>
              ) : (
                <div className="mb-4"></div>
              )}
            </div>

            <div>
              <MaterialTableTS
                data={
                  paymentRemittanceTypeFilteredData?.length > 0
                    ? paymentRemittanceTypeFilteredData
                    : filterTextState === ""
                    ? paymentRemittanceTypeData
                    : filteredItems
                }
                showTitle={true}
                columns={matColumns}
                onSelectionChange={handleSelectedRowsChange}
                selectedRows={selectedRows}
                selection={userPermissions.includes(deletePermission)}
                isLoading={isFetching}
                setConfirmModalOpen={setOpenModalDelete}
              />
            </div>

            <ConfirmBoxDelete
              ModalTitle="Deactivate PaymentRemittanceType"
              qConfirmation={deleteMessage}
              isOpen={openModalDelete}
              onClose={() => {
                setOpenModalDelete(false);
                setSelectedRows([]);
              }}
              onClickFunction={handleDelete}
              cancelButtonRef={undefined}
            />
          </>
        )}
      </div>
    </>
  );
}
